/**
 * SideNavItem — spec'd sidebar nav row.
 * Inactive: #E6FEFF @70% · Active: bg #014751 + 3px left border #37F4B5, mint icon/text bold · Hover: bg #014751, white.
 * Collapsed (60px rail): icon only + tooltip (bg #014751, text #E6FEFF) to the right.
 */
import { NavLink } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export function navRowClass(isActive, collapsed, size = "md") {
  const h = size === "sm" ? "min-h-[40px] text-[13px]" : "min-h-[52px] text-[14px]";
  return [
    "group relative flex items-center gap-3 rounded-lg font-medium transition-colors border-l-[3px] w-full",
    h,
    collapsed ? "justify-center px-0" : "pl-[15px] pr-3",
    isActive ? "bg-hocp-deepTeal border-hocp-mint text-hocp-mint font-bold" : "border-transparent text-hocp-softIce/70 hover:bg-hocp-deepTeal hover:text-white",
  ].join(" ");
}

export function withTooltip(collapsed, label, node) {
  if (!collapsed) return node;
  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>{node}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8} className="bg-hocp-deepTeal text-hocp-softIce text-[12px] border-0 px-2.5 py-1.5 rounded-md">{label}</TooltipContent>
    </Tooltip>
  );
}

export function SideNavItem({ to, label, icon: Icon, end, collapsed, testid, onNavigate, size = "md" }) {
  const iconSize = size === "sm" ? 18 : 22;
  return withTooltip(collapsed, label, (
    <NavLink to={to} end={end} data-testid={testid} onClick={onNavigate} aria-label={label}
      className={({ isActive }) => navRowClass(isActive, collapsed, size)}>
      {({ isActive }) => (
        <>
          {Icon && <Icon size={iconSize} strokeWidth={1.5} className={`shrink-0 ${isActive ? "text-hocp-mint" : "text-hocp-softIce/70 group-hover:text-white"}`} />}
          {!collapsed && <span className="truncate">{label}</span>}
        </>
      )}
    </NavLink>
  ));
}

export function SideNavButton({ label, icon: Icon, collapsed, onClick, testid, active, size = "md", trailing }) {
  const iconSize = size === "sm" ? 18 : 22;
  return withTooltip(collapsed, label, (
    <button type="button" onClick={onClick} data-testid={testid} aria-label={label} className={navRowClass(!!active, collapsed, size)}>
      {Icon && <Icon size={iconSize} strokeWidth={1.5} className={`shrink-0 ${active ? "text-hocp-mint" : "text-hocp-softIce/70 group-hover:text-white"}`} />}
      {!collapsed && <span className="flex-1 text-left truncate">{label}</span>}
      {!collapsed && trailing}
    </button>
  ));
}

export function NavSeparator({ collapsed }) {
  return <div className={`h-px bg-hocp-deepTeal my-2 mx-auto ${collapsed ? "w-[30px]" : "w-[calc(100%-24px)]"}`} aria-hidden />;
}
